import { Link, useNavigate, useParams, useSearch } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import { CsvIoButtons } from "~/components/CsvIoButtons";
import { DealsBulkBar } from "~/components/DealsBulkBar";
import { KanbanBoard } from "~/components/KanbanBoard";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import { TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import { useCreateDeal, useDeals, useMe, usePipelines, useStages } from "~/lib/hooks";
import { useRowSelection } from "~/lib/selection";
import type { DealListItem } from "~/lib/types";
import type { DealStatus } from "~/lib/types";

type ViewMode = "table" | "board";
const VIEW_KEY = "scaicrm.deals.view";

function loadView(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_KEY);
    if (stored === "table" || stored === "board") return stored;
  } catch {
    /* fall through */
  }
  return "table";
}

const STATUS_BADGE: Record<DealStatus, string> = {
  open: "badge-outline",
  won: "badge-success",
  lost: "badge-error",
  abandoned: "badge-ghost",
};

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

export function DealsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const navigate = useNavigate();

  const pipelines = usePipelines(() => params().wsKey);
  const [selectedPipelineId, setSelectedPipelineId] = createSignal<string | undefined>();

  // Resolve the active pipeline: explicit choice, else the default.
  const activePipelineId = createMemo<string | undefined>(() => {
    const chosen = selectedPipelineId();
    if (chosen) return chosen;
    return pipelines.data?.find((p) => p.is_default)?.id ?? pipelines.data?.[0]?.id;
  });

  const stages = useStages(() => params().wsKey, activePipelineId);

  const [stageFilter, setStageFilter] = createSignal<string>("");
  const [statusFilter, setStatusFilter] = createSignal<DealStatus | "">("open");
  // `?stalled=14` in the URL pre-activates the toggle so the dashboard link
  // lands on a filtered view. The URL drives the initial value; user toggles
  // override the URL signal until next navigation.
  const search = useSearch({ strict: false }) as () => { stalled?: string | number };
  const parseStalled = (raw: string | number | undefined): number | null => {
    const n = raw === undefined || raw === "" ? NaN : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const [stalledOverride, setStalledOverride] = createSignal<number | null | "url">(
    "url",
  );
  const stalledDays = (): number | null => {
    const o = stalledOverride();
    if (o === "url") return parseStalled(search().stalled);
    return o;
  };
  const [viewMode, setViewModeSignal] = createSignal<ViewMode>(loadView());
  const setViewMode = (mode: ViewMode) => {
    setViewModeSignal(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const deals = useDeals(
    () => params().wsKey,
    () => ({
      pipeline_id: activePipelineId(),
      stage_id: stageFilter() || undefined,
      status: statusFilter() || undefined,
      owner_user_id: ownerFilter() ?? undefined,
      stalled_days: stalledDays() ?? undefined,
      limit: 100,
    }),
  );

  const visibleRows = () => deals.data?.items ?? [];
  const selection = useRowSelection<DealListItem>(() =>
    visibleRows().map((r) => r.id),
  );

  const [showCreate, setShowCreate] = createSignal(false);
  const createMutation = useCreateDeal(() => params().wsKey);
  const [createError, setCreateError] = createSignal<string | null>(null);
  const [createTags, setCreateTags] = createSignal<string[]>([]);
  const [createOwner, setCreateOwner] = createSignal<string | null>(null);
  const [createOwnerName, setCreateOwnerName] = createSignal<string | null>(null);
  const [ownerFilter, setOwnerFilter] = createSignal<string | null>(null);
  const [ownerFilterName, setOwnerFilterName] = createSignal<string | null>(null);
  const me = useMe();

  const handleCreate = async (e: SubmitEvent) => {
    e.preventDefault();
    setCreateError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    if (!name) return;
    const amountStr = String(data.get("amount") ?? "").trim();
    try {
      const deal = await createMutation.mutateAsync({
        name,
        amount: amountStr ? Number(amountStr) : null,
        currency: String(data.get("currency") ?? "EUR") || null,
        pipeline_id: activePipelineId() ?? null,
        expected_close_date: String(data.get("expected_close_date") ?? "") || null,
        tags: createTags().length > 0 ? createTags() : undefined,
        owner_user_id: createOwner() ?? undefined,
      });
      setShowCreate(false);
      setCreateTags([]);
      setCreateOwner(null);
      setCreateOwnerName(null);
      void navigate({
        to: "/workspaces/$wsKey/deals/$dealId",
        params: { wsKey: params().wsKey, dealId: deal.id },
      });
    } catch (err) {
      setCreateError((err as Error).message);
    }
  };

  const stageMap = createMemo(() => Object.fromEntries((stages.data ?? []).map((s) => [s.id, s])));

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Deals"
      rightSlot={
        <div class="flex items-center gap-2">
          <div class="join" role="group" aria-label="Deals view mode">
            <button
              type="button"
              class={`btn btn-sm join-item ${viewMode() === "table" ? "btn-active" : ""}`}
              aria-pressed={viewMode() === "table"}
              onClick={() => setViewMode("table")}
            >
              Table
            </button>
            <button
              type="button"
              class={`btn btn-sm join-item ${viewMode() === "board" ? "btn-active" : ""}`}
              aria-pressed={viewMode() === "board"}
              onClick={() => setViewMode("board")}
            >
              Board
            </button>
          </div>
          <CsvIoButtons wsKey={() => params().wsKey} plural="deals" />
          <button class="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            New deal
          </button>
        </div>
      }
    >
      <div class="flex flex-col gap-4">
        <SavedViewsBar
          wsKey={() => params().wsKey}
          entityType={() => "deal"}
          currentFilters={() => ({
            pipeline_id: selectedPipelineId() ?? undefined,
            stage_id: stageFilter() || undefined,
            status: statusFilter() || undefined,
            owner_user_id: ownerFilter() ?? undefined,
            owner_user_name: ownerFilterName() ?? undefined,
            view_mode: viewMode(),
          })}
          onApply={(f) => {
            setSelectedPipelineId((f.pipeline_id as string) ?? undefined);
            setStageFilter((f.stage_id as string) ?? "");
            setStatusFilter((f.status as DealStatus | "") ?? "");
            setOwnerFilter((f.owner_user_id as string) ?? null);
            setOwnerFilterName((f.owner_user_name as string) ?? null);
            const vm = f.view_mode as ViewMode | undefined;
            if (vm === "table" || vm === "board") setViewMode(vm);
          }}
          onClear={() => {
            setSelectedPipelineId(undefined);
            setStageFilter("");
            setStatusFilter("open");
            setOwnerFilter(null);
            setOwnerFilterName(null);
          }}
        />
        <div class="flex gap-2 flex-wrap items-center">
          <label for="deals-pipeline" class="text-sm opacity-70">
            Pipeline:
          </label>
          <select
            id="deals-pipeline"
            class="select select-bordered select-sm"
            value={activePipelineId() ?? ""}
            onChange={(e) => setSelectedPipelineId(e.currentTarget.value || undefined)}
            disabled={!pipelines.data?.length}
          >
            <For each={pipelines.data ?? []}>
              {(p) => (
                <option value={p.id}>
                  {p.name} {p.is_default ? "(default)" : ""}
                </option>
              )}
            </For>
          </select>

          <label for="deals-stage" class="text-sm opacity-70 ml-2">
            Stage:
          </label>
          <select
            id="deals-stage"
            class="select select-bordered select-sm"
            value={stageFilter()}
            onChange={(e) => setStageFilter(e.currentTarget.value)}
          >
            <option value="">All</option>
            <For each={stages.data ?? []}>{(s) => <option value={s.id}>{s.name}</option>}</For>
          </select>

          <label for="deals-status" class="text-sm opacity-70 ml-2">
            Status:
          </label>
          <select
            id="deals-status"
            class="select select-bordered select-sm"
            value={statusFilter()}
            onChange={(e) => setStatusFilter(e.currentTarget.value as DealStatus | "")}
          >
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="abandoned">Abandoned</option>
            <option value="">All</option>
          </select>

          <label class="text-sm opacity-70 ml-2">Owner:</label>
          <div class="w-60">
            <UserPicker
              wsKey={() => params().wsKey}
              value={ownerFilter}
              selectedName={ownerFilterName}
              onChange={(id, name) => {
                setOwnerFilter(id);
                setOwnerFilterName(name);
              }}
              placeholder="Filter by owner…"
              label="Filter by owner"
              size="sm"
            />
          </div>
          <Show when={me.data?.user_id && me.data.user_id !== ownerFilter()}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => {
                setOwnerFilter(me.data!.user_id!);
                setOwnerFilterName(me.data?.display_name ?? me.data?.email ?? "Me");
              }}
            >
              Mine
            </button>
          </Show>
          <label class="cursor-pointer label gap-2 ml-2">
            <input
              type="checkbox"
              class="toggle toggle-sm toggle-warning"
              checked={stalledDays() !== null}
              onChange={(e) =>
                setStalledOverride(e.currentTarget.checked ? 14 : null)
              }
            />
            <span class="label-text text-sm">Stalled only (14d+)</span>
          </label>
        </div>

        <Show when={viewMode() === "board"}>
          <KanbanBoard
            wsKey={() => params().wsKey}
            pipelineId={activePipelineId}
            statusFilter={statusFilter}
          />
        </Show>

        <Show when={viewMode() === "table" && deals.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
            Loading deals…
          </div>
        </Show>
        <Show when={viewMode() === "table" && deals.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load deals.
          </div>
        </Show>

        <Show when={viewMode() === "table"}>
          <DealsBulkBar
            wsKey={() => params().wsKey}
            pipelineId={activePipelineId}
            count={selection.count}
            rows={() => selection.selectedRows(visibleRows())}
            onClear={selection.clear}
          />
        </Show>

        <Show when={viewMode() === "table" && deals.data}>
          {(data) => (
            <div class="overflow-x-auto bg-base-100 rounded-lg shadow-sm">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="w-8">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label="Select all deals on this page"
                        checked={selection.allSelected()}
                        ref={(el) => {
                          if (el) el.indeterminate = selection.someSelected();
                        }}
                        onChange={(e) =>
                          e.currentTarget.checked
                            ? selection.selectAll()
                            : selection.clear()
                        }
                      />
                    </th>
                    <th>Name</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Expected close</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={data().items}>
                    {(d) => (
                      <tr
                        class={`hover ${selection.has(d.id) ? "bg-base-200" : ""}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            aria-label={`Select ${d.name}`}
                            checked={selection.has(d.id)}
                            onChange={() => selection.toggle(d.id)}
                          />
                        </td>
                        <td>
                          <Link
                            to="/workspaces/$wsKey/deals/$dealId"
                            params={{ wsKey: params().wsKey, dealId: d.id }}
                            class="link link-primary font-medium"
                          >
                            {d.name}
                          </Link>
                        </td>
                        <td class="text-sm">
                          {stageMap()[d.stage_id]?.name ?? d.stage_id.slice(0, 12)}
                        </td>
                        <td>
                          <span class={`badge badge-sm ${STATUS_BADGE[d.status]}`}>{d.status}</span>
                        </td>
                        <td class="text-sm font-medium">{formatMoney(d.amount, d.currency)}</td>
                        <td class="text-sm opacity-80">
                          {d.expected_close_date
                            ? new Date(d.expected_close_date).toLocaleDateString()
                            : "—"}
                        </td>
                        <td class="text-xs opacity-60">
                          {new Date(d.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <Show when={data().items.length === 0}>
                <div class="text-center py-12 opacity-60">No deals match.</div>
              </Show>
            </div>
          )}
        </Show>
      </div>

      <Show when={showCreate()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="new-deal-title">
          <div class="modal-box">
            <h3 id="new-deal-title" class="font-bold text-lg mb-4">
              New deal
            </h3>
            <form onSubmit={handleCreate} class="flex flex-col gap-3">
              <input
                name="name"
                class="input input-bordered"
                placeholder="Deal name *"
                aria-label="Deal name"
                required
              />
              <div class="grid grid-cols-2 gap-3">
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  class="input input-bordered"
                  placeholder="Amount"
                  aria-label="Amount"
                />
                <input
                  name="currency"
                  class="input input-bordered"
                  placeholder="Currency"
                  aria-label="Currency"
                  maxLength={3}
                  value="EUR"
                />
              </div>
              <label class="label">
                <span class="label-text">Expected close date</span>
              </label>
              <input
                name="expected_close_date"
                type="date"
                class="input input-bordered"
                aria-label="Expected close date"
              />
              <div>
                <label class="label">
                  <span class="label-text">Tags</span>
                </label>
                <TagsEditor
                  wsKey={() => params().wsKey}
                  entityType="deal"
                  value={createTags}
                  onChange={setCreateTags}
                />
              </div>
              <div>
                <label class="label">
                  <span class="label-text">Owner</span>
                </label>
                <UserPicker
                  wsKey={() => params().wsKey}
                  value={createOwner}
                  selectedName={createOwnerName}
                  onChange={(id, name) => {
                    setCreateOwner(id);
                    setCreateOwnerName(name);
                  }}
                />
              </div>
              <Show when={createError()}>
                <div role="alert" class="alert alert-error text-sm">
                  {createError()}
                </div>
              </Show>
              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateTags([]);
                    setCreateOwner(null);
                    setCreateOwnerName(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>
    </AppShell>
  );
}
